import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES, LOGO } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { Reveal, Chapter } from "@/components/motion";

export default function About() {
  return (
    <>
      <Seo
        title="About Porter New Orleans | Professional Organizer & Personal Concierge"
        description="Porter New Orleans was founded in 2016 by Kristina Fortier — a New Orleans native, busy mom, and former corporate executive. Professional organization and personal concierge, done personally."
        path="/about"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 pt-16 sm:pt-24" data-testid="about-hero">
        <Reveal y={16}>
          <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold-dark">
            <span className="h-px w-8 bg-gold" aria-hidden="true" /> About Porter
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-7 max-w-4xl font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
            The hands, wheels, and brains <em className="text-gold-dark">behind Porter.</em>
          </h1>
        </Reveal>
      </section>

      <section className="bg-white py-24 lg:py-28" data-testid="about-story">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Chapter number="01" label="The beginning" />
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl leading-snug text-navy">
              Founded in 2016, born from a life lived at full speed.
            </h2>
            <p className="mt-6 leading-relaxed text-navy/70">
              Porter New Orleans was founded by Kristina Fortier — a New Orleans native, a busy mom,
              and a former corporate executive. She spent years balancing boardrooms and carpools,
              deadlines and dinner — and she noticed that the most capable people around her all shared
              the same quiet wish: an extra set of hands, wheels, or brains.
            </p>
            <p className="mt-5 leading-relaxed text-navy/70">
              So in 2016, she built Porter New Orleans around exactly that idea — a professional
              organization and personal concierge service that gives busy people their time back,
              handled with the care of someone who genuinely understands what a full household carries.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-16">
              <Chapter number="02" label="The way we work" />
              <h2 className="mt-6 font-serif text-3xl sm:text-4xl leading-snug text-navy">
                Personal, precise, and quietly efficient.
              </h2>
              <p className="mt-6 leading-relaxed text-navy/70">
                There is no rotating cast of helpers here. Porter brings corporate-honed efficiency,
                an eye for detail, and the belief that an organized home changes how a whole day feels.
                It&rsquo;s why so much of Porter&rsquo;s work comes from repeat clients and the friends
                they refer.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Personalized attention — never a template",
                  "Attention to detail that borders on devotion",
                  "Efficiency shaped by years in the corporate world",
                  "Deep local knowledge of New Orleans and its rhythms",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-4 text-navy/80">
                    <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="about-cta-button"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold tracking-wide text-cream transition-all duration-300 hover:bg-navy-light hover:-translate-y-0.5"
              >
                Get in Touch <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Chapter number="03" label="Rooted here" />
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">
              New Orleans isn&rsquo;t just where we work. <em className="text-gold-dark">It&rsquo;s home.</em>
            </h2>
            <p className="mt-7 max-w-lg leading-relaxed text-navy/70">
              From Uptown to Lakeview and everywhere in between, Porter serves the Greater New Orleans
              Area with a native&rsquo;s knowledge of the city — its vendors, its seasons, its storms,
              and its celebrations.
            </p>
            <div className="mt-10 rounded-3xl border border-navy/10 bg-white p-6">
              <img
                src={LOGO}
                alt="Porter New Orleans professional organizer and personal concierge service logo"
                loading="lazy"
                className="mx-auto w-full max-w-sm rounded-lg"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl">
              <img
                src={IMAGES.nola}
                alt="Elegant balconies in the New Orleans French Quarter"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.04] lg:aspect-[3/4]"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
